/**
 * SOFSEK — Application Registry (§1). Master registry of external systems (and
 * SOFSEK's own self-row, D1) that participate in cross-application SoD scans.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup, type RelatedChildItem } from '../../components/common/RelatedChildrenPanel';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.APPLICATION.LIST';
const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface DistModeOpt { distribution_mode_code: string; tl: { locale_code: string; display_text: string }[] }

interface AppRow {
  application_id: number;
  internal_code: string;
  environment_label: string | null;
  distribution_mode_code: string;
  stale_txn_threshold_days: number | null;
  reconciliation_expected: 'Y' | 'N';
  is_self: 'Y' | 'N';
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'display_text', header: 'Application', defaultWidth: 220 },
  { key: 'internal_code', header: 'Code', defaultWidth: 140 },
  { key: 'distribution_mode', header: 'Distribution Mode', defaultWidth: 180 },
  { key: 'reconciliation_expected', header: 'Reconciliation', defaultWidth: 120 },
  { key: 'is_self', header: 'Self', defaultWidth: 80 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function tlName(tl: { locale_code: string; display_text: string }[], loc: string): string {
  return tl.find(x => x.locale_code === loc)?.display_text ?? tl.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

function ApplicationInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [modes, setModes] = useState<DistModeOpt[]>([]);
  const [permsByApp, setPermsByApp] = useState<Map<number, RelatedChildItem[]>>(new Map());
  const [rolesByApp, setRolesByApp] = useState<Map<number, RelatedChildItem[]>>(new Map());
  const [accountsByApp, setAccountsByApp] = useState<Map<number, RelatedChildItem[]>>(new Map());
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [modeFacet, setModeFacet] = useState<string[]>([]);
  const [editing, setEditing] = useState<AppRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [appRes, modeRes, permRes, roleRes, acctRes] = await Promise.all([
        apiDb.from('sfk_application').select(`
          application_id, environment_label, distribution_mode_code,
          stale_txn_threshold_days, reconciliation_expected, is_self, is_active,
          csm_application_master ( internal_code, csm_application_master_tl ( locale_code, display_text ) )
        `).order('application_id'),
        apiDb.from('sfk_distribution_mode').select('distribution_mode_code, sfk_distribution_mode_tl ( locale_code, display_text )'),
        // §17.B13 children — atomic permissions defined in each application (active).
        apiDb.from('sfk_v_action').select('application_id, permission_id, display_name, platform_signature').eq('is_active', 'Y').order('display_name'),
        // §17.B13 children — external roles defined in each application (active).
        apiDb.from('sfk_external_role').select('application_id, role_id, name, internal_code').eq('is_active', 'Y').order('name'),
        // §17.B13 children — app-user accounts seen in each application (active).
        apiDb.from('sfk_app_user_account').select('application_id, app_user_account_id, external_username, csm_user_master!app_user_id ( user_id, display_name )').eq('is_active', 'Y'),
      ]);
      if (appRes.error) throw appRes.error;
      if (modeRes.error) throw modeRes.error;
      if (permRes.error) throw permRes.error;
      if (roleRes.error) throw roleRes.error;
      if (acctRes.error) throw acctRes.error;

      const permsMap = new Map<number, RelatedChildItem[]>();
      for (const r of (permRes.data ?? []) as unknown as Record<string, unknown>[]) {
        const appId = r.application_id as number;
        const list = permsMap.get(appId) ?? [];
        list.push({ id: r.permission_id as number, label: r.display_name as string, title: r.platform_signature as string });
        permsMap.set(appId, list);
      }
      setPermsByApp(permsMap);

      const rolesMap = new Map<number, RelatedChildItem[]>();
      for (const r of (roleRes.data ?? []) as unknown as Record<string, unknown>[]) {
        const appId = r.application_id as number;
        const list = rolesMap.get(appId) ?? [];
        list.push({ id: r.role_id as number, label: r.name as string, title: r.internal_code as string });
        rolesMap.set(appId, list);
      }
      setRolesByApp(rolesMap);

      const acctMap = new Map<number, RelatedChildItem[]>();
      for (const r of (acctRes.data ?? []) as unknown as Record<string, unknown>[]) {
        const appId = r.application_id as number;
        const user = r.csm_user_master as { user_id: number; display_name: string } | null;
        const list = acctMap.get(appId) ?? [];
        list.push({ id: r.app_user_account_id as number, label: user?.display_name ?? (r.external_username as string), title: r.external_username as string });
        acctMap.set(appId, list);
      }
      setAccountsByApp(acctMap);
      setRows(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => {
        const parent = r.csm_application_master as { internal_code: string; csm_application_master_tl?: AppRow['tl'] } | null;
        return {
          application_id: r.application_id as number,
          internal_code: parent?.internal_code ?? String(r.application_id),
          environment_label: r.environment_label as string | null,
          distribution_mode_code: r.distribution_mode_code as string,
          stale_txn_threshold_days: r.stale_txn_threshold_days as number | null,
          reconciliation_expected: r.reconciliation_expected as 'Y' | 'N',
          is_self: r.is_self as 'Y' | 'N',
          is_active: r.is_active as 'Y' | 'N',
          tl: (parent?.csm_application_master_tl ?? []) as AppRow['tl'],
        };
      }));
      setModes(((modeRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        distribution_mode_code: r.distribution_mode_code as string,
        tl: (r.sfk_distribution_mode_tl ?? []) as DistModeOpt['tl'],
      })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForMode = (code: string) => tlName(modes.find(m => m.distribution_mode_code === code)?.tl ?? [], locale) || code;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.internal_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active)) &&
      (modeFacet.length === 0 || modeFacet.includes(r.distribution_mode_code)));
  }, [rows, filter, activeFacet, modeFacet, locale]);

  const hasRelated = (id: number) =>
    (permsByApp.get(id)?.length ?? 0) > 0 || (rolesByApp.get(id)?.length ?? 0) > 0 || (accountsByApp.get(id)?.length ?? 0) > 0;
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    { key: 'permissions', title: t('sfk.application.related.permissions', 'Atomic Permissions'), items: permsByApp.get(id) ?? [] },
    { key: 'roles', title: t('sfk.application.related.roles', 'Roles'), items: rolesByApp.get(id) ?? [] },
    { key: 'accounts', title: t('sfk.application.related.accounts', 'User Accounts'), items: accountsByApp.get(id) ?? [] },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.application.title', 'Application Registry')}
          description={t('sfk.application.description', 'External systems (and SOFSEK itself) that participate in cross-application SoD scans.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rows.length, label: t('sfk.application.stat.total', 'Applications') },
          { value: rows.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.application.facet.mode', 'Distribution')} options={modes.map(m => ({ value: m.distribution_mode_code, label: resolveNameForMode(m.distribution_mode_code) }))} selected={modeFacet} onChange={setModeFacet} />
            <FacetFilter label={t('common.boolean.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]} selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || activeFacet.length > 0 || modeFacet.length > 0} onClear={() => { setFilter(''); setActiveFacet([]); setModeFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('sfk.application.related.header', 'Permissions, roles & accounts')}></th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.application_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.application_id)}
                        hasChildren={hasRelated(r.application_id)}
                        onToggle={() => detail.toggle(r.application_id)}
                        expandLabel={t('sfk.application.related.expand_aria', 'Show related records')}
                        collapseLabel={t('sfk.application.related.collapse_aria', 'Hide related')}
                        tooltip={t('sfk.application.related.tooltip', 'Show permissions, roles & accounts')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'display_text' && tlName(r.tl, locale)}
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>} {/* fk-display-ok: own registry code */}
                        {col.key === 'distribution_mode' && resolveNameForMode(r.distribution_mode_code)}
                        {col.key === 'reconciliation_expected' && <YesNoBadge value={r.reconciliation_expected} />}
                        {col.key === 'is_self' && <YesNoBadge value={r.is_self} />}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.application_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.application_id)} emptyLabel={t('sfk.application.related.empty', 'No permissions, roles or accounts.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <ApplicationModal session={session} row={editing} modes={modes} locale={locale}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function ApplicationModal({ session, row, modes, locale, onClose, onSaved }: {
  session: AuthSession; row: AppRow | null; modes: DistModeOpt[]; locale: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [envLabel, setEnvLabel] = useState(row?.environment_label ?? '');
  const [distMode, setDistMode] = useState(row?.distribution_mode_code ?? modes[0]?.distribution_mode_code ?? 'REPORT');
  const [staleThreshold, setStaleThreshold] = useState(row?.stale_txn_threshold_days != null ? String(row.stale_txn_threshold_days) : '');
  const [reconciliationExpected, setReconciliationExpected] = useState(!row || row.reconciliation_expected === 'Y');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(
    row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' }))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      // The SOFSEK monitoring attributes live on the sfk_application extension.
      const extPayload = {
        environment_label: envLabel.trim() || null,
        distribution_mode_code: distMode,
        stale_txn_threshold_days: staleThreshold.trim() ? Number(staleThreshold) : null,
        reconciliation_expected: reconciliationExpected ? 'Y' : 'N',
        is_active: isActive ? 'Y' : 'N',
      };
      let applicationId = row?.application_id;
      if (row === null) {
        // One add screen writes BOTH rows: the generic identity in
        // csm_application_master, then the SOFSEK extension keyed by its id.
        const { row_id, error: e } = await routedInsert(session, 'csm_application_master', { internal_code: code.trim().toUpperCase(), is_active: 'Y' });
        if (e) throw new Error(e.message);
        applicationId = row_id ?? undefined;
        if (applicationId != null) {
          const { error: e2 } = await routedInsert(session, 'sfk_application', { application_id: applicationId, is_self: 'N', ...extPayload });
          if (e2) throw new Error(e2.message);
        }
      } else {
        const { error: e } = await routedUpdate(session, 'sfk_application', 'application_id', row.application_id, extPayload);
        if (e) throw new Error(e.message);
      }
      // Display text is identity-level → csm_application_master_tl (create + edit).
      if (applicationId != null) {
        await Promise.all(
          translations.filter(tr => tr.display_text?.trim()).map(tr =>
            routedInsert(session, 'csm_application_master_tl', {
              application_id: applicationId,
              locale_code: tr.locale_code,
              display_text: tr.display_text,
            }))
        );
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('sfk.application.new', 'New Application') : `${t('common.edit', 'Edit')} — ${row?.internal_code ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} placeholder="SAP_PROD" />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="environment_label" label={t('sfk.application.env_label', 'Environment Label')}>
        <label className={labelCls}>{t('sfk.application.env_label', 'Environment Label')}</label>
        <input value={envLabel} onChange={e => setEnvLabel(e.target.value)} className={inputCls} placeholder={t('sfk.application.env_label_placeholder', 'Production')} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="distribution_mode_code" label={t('sfk.application.dist_mode', 'Distribution Mode')}>
        <label className={labelCls}>{t('sfk.application.dist_mode', 'Distribution Mode')}</label>
        <select value={distMode} onChange={e => setDistMode(e.target.value)} className={`${inputCls} bg-white`}>
          {modes.map(m => <option key={m.distribution_mode_code} value={m.distribution_mode_code}>{tlName(m.tl, locale)}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="stale_txn_threshold_days" label={t('sfk.application.stale_threshold', 'Stale Open-Transaction Threshold (days)')}>
        <label className={labelCls}>{t('sfk.application.stale_threshold', 'Stale Open-Transaction Threshold (days)')}</label>
        <input type="number" min="1" value={staleThreshold} onChange={e => setStaleThreshold(e.target.value)} className={inputCls} placeholder={t('sfk.application.stale_threshold_never', 'Blank = never')} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="reconciliation_expected" label={t('sfk.application.reconciliation_expected', 'Reconciliation Expected')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={reconciliationExpected} onChange={e => setReconciliationExpected(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('sfk.application.reconciliation_expected', 'Reconciliation Expected')}
        </label>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkApplicationPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <ApplicationInner session={session} />
    </PersonalizationProvider>
  );
}
